import React from 'react';

// Base atom — a shimmer block. Compose with Tailwind sizing classes.
// Example: <Skeleton className="h-4 w-24" />
export const Skeleton = ({ className = '' }) => (
  <div className={`bg-gray-200 dark:bg-gray-800 rounded animate-pulse ${className}`} />
);

// Stacked rows. Default shape covers EI/CI list & table-row placeholders.
// `lineClass` tunes row height (h-8 / h-10 / h-12). `gapClass` tunes vertical
// spacing — callers needing space-y-3 etc. should pass it explicitly.
export const SkeletonText = ({ lines = 5, lineClass = 'h-8', gapClass = 'space-y-2', className = '' }) => (
  <div className={`${gapClass} ${className}`}>
    {Array.from({ length: lines }).map((_, i) => (
      <Skeleton key={i} className={lineClass} />
    ))}
  </div>
);

// Single tall block sized like a chart body. Replaces the GA4 spinner-in-card
// pattern and EI's inline chart placeholders. Lighter background than the base
// atom to match EI's existing visual weight. Callers pass rounded-* via className
// (default: rounded-lg) so chart cards with rounded-xl can override.
export const SkeletonChart = ({ height = 280, className = 'rounded-lg' }) => (
  <div
    className={`bg-gray-100 dark:bg-gray-800/50 animate-pulse ${className}`}
    style={{ height }}
  />
);

// Matches the visual weight of MetricCard / KpiCard: small label, big number,
// trailing subtitle.
export const SkeletonMetricCard = ({ className = '' }) => (
  <div
    className={`bg-white dark:bg-gray-900 rounded-xl shadow-sm border dark:border-gray-800 border-t-[3px] border-t-gray-200 dark:border-t-gray-700 p-4 flex flex-col animate-pulse ${className}`}
  >
    <div className="h-3 w-28 bg-gray-200 dark:bg-gray-800 rounded" />
    <div className="h-8 w-24 bg-gray-200 dark:bg-gray-800 rounded mt-3" />
    <div className="h-3 w-32 bg-gray-200 dark:bg-gray-800 rounded mt-auto" />
  </div>
);

// Medium card with a header line and a few body lines. Used by CI's
// AiPricingActions / CompetitorProfiles tile grids.
export const SkeletonCard = ({ lines = 3, className = '' }) => (
  <div
    className={`bg-white dark:bg-gray-900 rounded-xl shadow border dark:border-gray-800 p-5 animate-pulse ${className}`}
  >
    <div className="h-3 w-24 bg-gray-200 dark:bg-gray-800 rounded" />
    <div className="h-4 w-2/3 bg-gray-200 dark:bg-gray-800 rounded mt-3" />
    {Array.from({ length: Math.max(0, lines - 1) }).map((_, i) => (
      <div
        key={i}
        className={`h-3 ${i === lines - 2 ? 'w-5/6' : 'w-full'} bg-gray-200 dark:bg-gray-800 rounded mt-2`}
      />
    ))}
  </div>
);

// Page-level loading shell for the analytics dashboards (AI Monitor, Route
// Intelligence): a KPI row, a tab strip, and a chart-sized content block.
// Mirrors the real page layout so a load reads as "already here" instead of a
// single centered "Loading…" line. `cards` matches the page's headline count
// (AIM = 3, RI = 4).
export const SkeletonDashboard = ({ cards = 4 }) => (
  <div role="status" aria-label="Loading" aria-live="polite">
    <div className={`grid gap-3 ${cards === 3 ? 'grid-cols-1 sm:grid-cols-3' : 'grid-cols-2 md:grid-cols-4'}`}>
      {Array.from({ length: cards }).map((_, i) => (
        <SkeletonMetricCard key={i} />
      ))}
    </div>
    <div className="mt-4 flex flex-wrap gap-2">
      {Array.from({ length: 6 }).map((_, i) => (
        <Skeleton key={i} className="h-7 w-24" />
      ))}
    </div>
    <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-3">
      <SkeletonChart className="rounded-xl lg:col-span-2" height={300} />
      <SkeletonChart className="rounded-xl" height={300} />
    </div>
    <span className="sr-only">Loading…</span>
  </div>
);

// Banner-shaped loading placeholder for the insight/alert cards (AI Monitor
// "What Changed", Route Intelligence "Key Findings"): an icon block, a short
// title line, and a few body lines — same shimmer treatment as the page
// skeleton, so the alert area reads as "loading" rather than a spinner or a
// text placeholder.
export const SkeletonAlert = ({ lines = 2 }) => (
  <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4" role="status" aria-label="Loading">
    <div className="flex items-start gap-3">
      <Skeleton className="h-5 w-5 shrink-0" />
      <div className="flex-1 space-y-2">
        <Skeleton className="h-3 w-32" />
        {Array.from({ length: lines }).map((_, i) => (
          <Skeleton key={i} className={`h-3 ${i === lines - 1 ? 'w-2/3' : 'w-full'}`} />
        ))}
      </div>
    </div>
    <span className="sr-only">Loading…</span>
  </div>
);

// Slim animated bar shown along the top of a card while a refetch is in
// flight. Used together with an `opacity-60` wrapper around the prior content
// so the user keeps their place (e.g. a table page click) instead of seeing the
// rows replaced by a skeleton on every navigation. The host element must be
// `position: relative` for the absolute positioning to anchor correctly.
export const RefetchBar = () => (
  <div data-testid="refetch-bar" className="absolute top-0 left-0 right-0 h-0.5 overflow-hidden z-10 pointer-events-none">
    <div className="h-full w-1/3 bg-[#32B1FF] animate-[refetch-bar_1s_ease-in-out_infinite]" />
    <style>{`
      @keyframes refetch-bar {
        0%   { transform: translateX(-100%); }
        100% { transform: translateX(400%); }
      }
    `}</style>
  </div>
);

// Universal Suspense fallback for route-level code splitting (React.lazy).
//
// Deliberately NEUTRAL — a centered spinner on a full-height, bg-matching area
// rather than a content-shaped skeleton. Each page renders its own header +
// sidebar and its own card sizes, so a generic dashboard skeleton here doesn't
// match the page that loads next: the cards would visibly reposition/resize
// (layout jump) when the real chunk mounts. A neutral full-screen placeholder
// has nothing to jump from, so the transition is smooth (and dark-mode safe).
export const SkeletonRoute = () => (
  <div
    className="flex items-center justify-center min-h-screen bg-[#f7f8fa] dark:bg-gray-950"
    role="status"
    aria-label="Loading"
    aria-live="polite"
  >
    <div className="spinner" />
    <span className="sr-only">Loading…</span>
  </div>
);

export default Skeleton;
